import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Modal,
    Button,
    TextInput,
    Box,
    Text,
    ActionIcon,
    Badge,
    Pagination,
    Select,
    Checkbox,
    Group,
    Stack,
    Tooltip,
    Flex
} from '@mantine/core';
import {
    IconSearch,
    IconPlus,
    IconMaximize,
    IconMinimize
} from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import {
    combineScimFiltersWithAnd,
    resolveSimpleSearchPlan,
    type SimpleSearchDialectMode,
} from '../../utils/simpleSearchUtils';
import EntityBrowserTable, { Entity as TableEntity } from '../entities/EntityBrowserTable';
import { debugLog } from '../../utils/debugLog';
import { DialogErrorAlert } from './DialogErrorAlert';
import { MODAL_SIZES } from '../../theme/mantineTheme';
import { useModalFullscreen } from '../../hooks/useModalFullscreen';

const ENTITY_BROWSER_SEARCH_ATTRIBUTES = ['DisplayName', 'Id'];

interface EntityBrowserDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called with the selected entities (Id, DisplayName, EntityType). Callers can derive ids via entities.map(e => e.Id). */
    onConfirm?: (selectedEntities: Array<{ Id: string; DisplayName: string; EntityType: string }>) => void;
    entityType: string;
    /** When provided (e.g. mixed reference), show entity type dropdown + search + table in one dialog, like Person. */
    entityTypes?: string[];
    title?: string;
    /** Shown in a tooltip on the dialog title so you can trace which control opened this dialog. */
    callerName: string;
    singleSelection?: boolean;
    filterFunction?: (entity: Entity) => boolean; // Optional filter function for entities
    preloadedEntities?: Entity[]; // Optional pre-loaded entities to use instead of loading from API
    scimFilter?: string; // Optional SCIM filter string to apply to API queries
    /** Optional entities that are already selected (to show in dialog). TODO: For very large counts, support paginated deletes and delay-load this list instead of passing the full array. */
    alreadySelectedEntities?: Entity[];
    /** When true, hide search UI (useful for property-path paginated viewers that don't support search). */
    disableSearch?: boolean;
    /** When true, hide sort UI (useful for property-path paginated viewers that don't support sort). */
    disableSort?: boolean;
    /** When true, dialog acts as a viewer (selection allowed, but no confirm/add button). */
    viewOnly?: boolean;
    /**
     * Optional override for how pages are loaded (useful for property-path endpoints like
     * api/scim/v2/entities/{entityType}/{entityId}/{propertyAttributeDisplayName}).
     * When provided, EntityBrowserDialog uses this instead of getEntitiesByType().
     */
    fetchPageOverride?: (args: {
        entityType: string;
        page: number;
        pageSize: number;
        sortBy: string;
        sortOrder: 'asc' | 'desc';
        searchQuery: string;
        scimFilter?: string;
        omitSearchDialectExpression?: boolean;
    }) => Promise<{ data: Entity[]; totalPages?: number; totalResults?: number }>;
    /** Modal size (e.g. 'xl', '90%', 1400). Default 'xl'. */
    modalSize?: string | number;
}

interface Entity {
    Id: string;
    DisplayName?: string;
    Name?: string;
    Type?: string;
    [key: string]: any;
}

const EntityBrowserDialog: React.FC<EntityBrowserDialogProps> = ({
    open,
    onClose,
    onConfirm,
    entityType,
    entityTypes: entityTypesProp,
    title,
    callerName,
    singleSelection = false,
    filterFunction,
    preloadedEntities,
    scimFilter,
    alreadySelectedEntities = [],
    disableSearch = false,
    disableSort = false,
    viewOnly = false,
    fetchPageOverride,
    modalSize = MODAL_SIZES.browser
}) => {
    const modalFs = useModalFullscreen(modalSize);
    const [entities, setEntities] = useState<Entity[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]); // Track full entity objects for selected items
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [sortBy, setSortBy] = useState<string>('DisplayName');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [activeSearchQuery, setActiveSearchQuery] = useState<string>('');
    const [isTableFullscreen, setIsTableFullscreen] = useState(false);
    const [personEntityTypeFilter, setPersonEntityTypeFilter] = useState<string>('all'); // Filter for Person entity types
    const [mixedTypeSelection, setMixedTypeSelection] = useState<string>(''); // When entityTypesProp: selected type for table
    const [omitSearchDialectExpression, setOmitSearchDialectExpression] = useState(false);
    const prevOpenRef = useRef(false);

    const itemsPerPage = 20;

    const isMixedTypeMode = Array.isArray(entityTypesProp) && entityTypesProp.length > 1;
    const effectiveEntityTypeForLoad = isMixedTypeMode ? mixedTypeSelection : entityType;

    // Check if this is a Person entity type (to show entity type filter)
    const isPersonEntity = entityType?.toLowerCase() === 'person';

    const handleFullscreenToggle = () => {
        setIsTableFullscreen(!isTableFullscreen);
    };

    // Load entities when dialog opens or search changes
    const loadEntities = useCallback(async () => {
        if (isMixedTypeMode && !mixedTypeSelection) return;
        if (!isMixedTypeMode && !entityType) return;

        setLoading(true);
        setError(null);

        const typeForLoad = isMixedTypeMode ? mixedTypeSelection : entityType;
        try {
            debugLog(`🔍 EntityBrowserDialog: Loading ${typeForLoad} entities, page ${currentPage}, search: "${activeSearchQuery}"`);

            let entities: Entity[] = [];
            let totalPages = 1;
            let totalResults = 0;

            if (preloadedEntities && !isMixedTypeMode) {
                // Use pre-loaded entities (not in mixed-type mode)
                debugLog(`🔍 EntityBrowserDialog: Using ${preloadedEntities.length} pre-loaded entities`);
                entities = preloadedEntities;
                totalPages = 1;
                totalResults = preloadedEntities.length;
            } else {
                let actualEntityType = entityType;
                if (isMixedTypeMode) {
                    actualEntityType = mixedTypeSelection;
                } else if (isPersonEntity && personEntityTypeFilter !== 'all') {
                    actualEntityType = personEntityTypeFilter;
                }

                const trimmedSearch = activeSearchQuery.trim();
                const dialectMode: SimpleSearchDialectMode = omitSearchDialectExpression ? 'freetext' : 'auto';
                const resolvedSearch = disableSearch
                    ? { rawQuery: trimmedSearch, dialect: 'freetext' as const }
                    : resolveSimpleSearchPlan(trimmedSearch, ENTITY_BROWSER_SEARCH_ATTRIBUTES, dialectMode);
                const searchtext = disableSearch ? undefined : resolvedSearch.searchText;
                const filter = combineScimFiltersWithAnd(scimFilter, resolvedSearch.filter);
                const effectiveOmitSearchDialect = resolvedSearch.dialect === 'freetext';

                if (fetchPageOverride) {
                    const response = await fetchPageOverride({
                        entityType: actualEntityType,
                        page: currentPage,
                        pageSize: itemsPerPage,
                        sortBy,
                        sortOrder,
                        searchQuery: activeSearchQuery,
                        scimFilter: filter,
                        omitSearchDialectExpression: effectiveOmitSearchDialect,
                    });
                    entities = response.data || [];
                    totalPages = response.totalPages || 1;
                    totalResults = response.totalResults || entities.length;
                } else {
                    debugLog(`🔍 EntityBrowserDialog: Loading ${actualEntityType} entities (original: ${entityType}), searchtext: ${searchtext ?? '(none)'}, filter: ${filter ?? '(none)'}`);

                    const response = await entityService.getEntitiesByType(
                        actualEntityType.toLowerCase(),
                        currentPage,
                        itemsPerPage,
                        sortBy,
                        filter,
                        undefined, // attributes
                        undefined, // abortSignal
                        searchtext,
                        'DisplayName,Id', // searchAttributes: same as simple search (DisplayName and Id)
                        undefined, // onBehalfOfUpn
                        undefined, // countOnly
                        effectiveOmitSearchDialect
                    );

                    entities = response.data || [];
                    totalPages = response.totalPages || 1;
                    totalResults = response.totalResults || 0;

                    debugLog(`📊 EntityBrowserDialog: Received ${entities.length} entities, totalResults: ${totalResults}, searchtext: ${!!searchtext}, filter: ${!!filter}`);
                }
            }

            // Apply search filter to pre-loaded entities if needed
            if (preloadedEntities && activeSearchQuery.trim()) {
                entities = entities.filter((entity: Entity) => {
                    const displayName = entity.DisplayName || entity.Name || entity.displayName || entity.name || '';
                    const searchLower = activeSearchQuery.toLowerCase();
                    return displayName.toLowerCase().includes(searchLower);
                });
            }

            // Apply filter function if provided
            if (filterFunction) {
                entities = entities.filter(filterFunction);
                debugLog(`🔍 EntityBrowserDialog: Filtered entities to ${entities.length} valid entities`);
            }

            setEntities(entities);
            setTotalPages(totalPages);
            setTotalResults(totalResults);

            debugLog(`📊 EntityBrowserDialog: Loaded ${entities.length} ${typeForLoad} entities`);
        } catch (err) {
            console.error(`❌ EntityBrowserDialog: Error loading ${typeForLoad} entities:`, err);
            setError(err instanceof Error ? err.message : `Failed to load ${typeForLoad} entities`);
        } finally {
            setLoading(false);
        }
    }, [entityType, currentPage, activeSearchQuery, sortBy, sortOrder, preloadedEntities, filterFunction, scimFilter, personEntityTypeFilter, isPersonEntity, isMixedTypeMode, mixedTypeSelection, omitSearchDialectExpression, fetchPageOverride, disableSearch]);

    // Load entities when dialog opens or dependencies change
    useEffect(() => {
        if (!open) return;
        if (isMixedTypeMode && !mixedTypeSelection) return;
        if (!isMixedTypeMode && !entityType) return;
        loadEntities();
    }, [open, entityType, loadEntities, isMixedTypeMode, mixedTypeSelection]);

    // Person entity types for filtering
    const personEntityTypes = ['Employee', 'Contractor', 'Student', 'Sponsored', 'Applicant', 'Public', 'Shared', 'Vacancy'];

    // Reset state only when dialog transitions from closed to open (not on every parent re-render).
    // Previously [open, alreadySelectedEntities] caused resets every time parent passed a new [] default,
    // wiping search input, page, and selection.
    useEffect(() => {
        const didOpen = open && !prevOpenRef.current;
        prevOpenRef.current = !!open;
        if (didOpen) {
            const alreadySelectedIds = alreadySelectedEntities.map(e => e.Id);
            setSelectedIds(alreadySelectedIds);
            setSelectedEntities([...alreadySelectedEntities]);
            setSearchQuery('');
            setCurrentPage(1);
            setError(null);
            setPersonEntityTypeFilter('all');
            if (isMixedTypeMode && entityTypesProp?.length) {
                setMixedTypeSelection(entityTypesProp[0]);
            }
        }
    }, [open, alreadySelectedEntities, isMixedTypeMode, entityTypesProp]);

    const handleSearchChange = (query: string) => {
        setSearchQuery(query);
        // Don't trigger search - wait for Enter or button click
    };

    const handleSearchSubmit = () => {
        setActiveSearchQuery(searchQuery);
        setCurrentPage(1); // Reset to first page when search changes
    };

    const handleSearchKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearchSubmit();
        }
    };

    const handleEntitySelect = (entityId: string, isSelected: boolean) => {
        if (singleSelection) {
            // For single selection, replace the current selection
            setSelectedIds(isSelected ? [entityId] : []);
            if (isSelected) {
                // Find the entity and add to selectedEntities
                const entity = entities.find(e => e.Id === entityId);
                if (entity) {
                    setSelectedEntities([entity]);
                }
            } else {
                setSelectedEntities([]);
            }
        } else {
            // For multi-selection, add/remove from the list
            if (isSelected) {
                setSelectedIds(prev => {
                    // Prevent duplicates by checking if already in the list
                    if (prev.includes(entityId)) {
                        return prev;
                    }
                    return [...prev, entityId];
                });
                // Find the entity and add to selectedEntities
                const entity = entities.find(e => e.Id === entityId);
                if (entity) {
                    setSelectedEntities(prev => {
                        // Prevent duplicates
                        if (prev.some(e => e.Id === entityId)) {
                            return prev;
                        }
                        return [...prev, entity];
                    });
                }
            } else {
                setSelectedIds(prev => prev.filter(id => id !== entityId));
                setSelectedEntities(prev => prev.filter(e => e.Id !== entityId));
            }
        }
    };

    const handleSelectAll = () => {
        if (singleSelection) {
            return; // No select all for single selection
        }

        if (selectedIds.length === entities.length) {
            // Deselect all visible entities (but keep ones from selectedEntities that aren't in current view)
            const visibleIds = entities.map(e => e.Id);
            setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
            setSelectedEntities(prev => prev.filter(e => !visibleIds.includes(e.Id)));
        } else {
            // Select all visible entities
            const allIds = entities.map(entity => entity.Id);
            setSelectedIds(prev => {
                const newIds = [...prev];
                entities.forEach(entity => {
                    if (!newIds.includes(entity.Id)) {
                        newIds.push(entity.Id);
                    }
                });
                return newIds;
            });
            setSelectedEntities(prev => {
                const newEntities = [...prev];
                entities.forEach(entity => {
                    if (!newEntities.some(e => e.Id === entity.Id)) {
                        newEntities.push(entity);
                    }
                });
                return newEntities;
            });
        }
    };

    const handleConfirm = () => {
        const typeForConfirm = isMixedTypeMode ? mixedTypeSelection : entityType;
        const entitiesToPass = selectedEntities.map((e) => ({
            Id: e.Id,
            DisplayName: (e.DisplayName ?? e.Name ?? e.Id ?? '').toString(),
            EntityType: (e as { EntityType?: string }).EntityType ?? e.Type ?? typeForConfirm ?? ''
        }));
        debugLog('[EntityBrowserDialog] selected value passed to onConfirm:', entitiesToPass, { singleSelection: !!singleSelection, entityType: typeForConfirm });
        onConfirm?.(entitiesToPass);
        onClose();
    };

    const handleClose = () => {
        // Don't clear selectedIds/selectedEntities on close - let parent component handle that
        setSearchQuery('');
        setCurrentPage(1);
        setPersonEntityTypeFilter('all');
        onClose();
    };

    const handleSortChange = (field: string) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const getDisplayName = (entity: Entity) => {
        return entity.DisplayName || entity.Name || entity.displayName || entity.name || entity.Id || 'N/A';
    };

    const getEntityType = (entity: Entity) => {
        return entity.Type || entity.type || entity.EntityType || (isMixedTypeMode ? mixedTypeSelection : entityType);
    };

    const isAllSelected = entities.length > 0 && selectedIds.length === entities.length;
    const isIndeterminate = selectedIds.length > 0 && selectedIds.length < entities.length;

    return (
        <Modal
            opened={open}
            onClose={handleClose}
            title={
                <Tooltip label={`EntityBrowserDialog - ${callerName}`} withArrow>
                    <Text fw={600} size="lg" component="span" style={{ cursor: 'help' }}>{title || `Select ${entityType} Entities`}</Text>
                </Tooltip>
            }
            size={modalFs.size}
            fullScreen={modalFs.fullScreen}
            styles={{
                body: { height: '90vh', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
            }}
        >
            <Stack gap="md" flex={1} mih={0} style={{ overflow: 'auto' }}>
                <DialogErrorAlert error={error} />

                <Box mb="md">
                    <Group gap="md" align="center" mb="md">
                        {!disableSearch && (
                            <TextInput
                                flex={1}
                                placeholder={`Search ${effectiveEntityTypeForLoad || entityType} entities... (Press Enter to search)`}
                                value={searchQuery}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                                leftSection={<IconSearch size={16} />}
                                rightSection={
                                    <ActionIcon variant="subtle" onClick={handleSearchSubmit} disabled={loading} size="sm">
                                        <IconSearch size={16} />
                                    </ActionIcon>
                                }
                                disabled={loading}
                            />
                        )}

                        {!disableSearch && (
                            <Checkbox
                                checked={omitSearchDialectExpression}
                                onChange={(e) => setOmitSearchDialectExpression(e.currentTarget.checked)}
                                size="sm"
                                label="Expression"
                            />
                        )}

                        {isMixedTypeMode && entityTypesProp && (
                            <Select
                                label="Entity Type"
                                value={mixedTypeSelection}
                                onChange={(v) => {
                                    setMixedTypeSelection(v ?? '');
                                    setCurrentPage(1);
                                }}
                                data={entityTypesProp.map((type) => ({ value: type, label: type }))}
                                disabled={loading}
                                miw={150}
                            />
                        )}
                        {!isMixedTypeMode && isPersonEntity && (
                            <Select
                                label="Entity Type"
                                value={personEntityTypeFilter}
                                onChange={(v) => {
                                    setPersonEntityTypeFilter(v ?? 'all');
                                    setCurrentPage(1);
                                }}
                                data={[
                                    { value: 'all', label: 'All' },
                                    ...personEntityTypes.map(type => ({ value: type, label: type }))
                                ]}
                                disabled={loading}
                                miw={150}
                            />
                        )}

                        {!disableSort && (
                            <Select
                                label="Sort By"
                                value={sortBy}
                                onChange={(v) => setSortBy(v ?? 'DisplayName')}
                                data={[
                                    { value: 'DisplayName', label: 'Display Name' },
                                    { value: 'Id', label: 'ID' },
                                    { value: 'Type', label: 'Type' }
                                ]}
                                disabled={loading}
                                miw={120}
                            />
                        )}
                    </Group>

                    <Group align="center" gap="xs" mb="md" mih={32}>
                        {selectedIds.length > 0 ? (
                            <>
                                <Badge color="blue" size="sm">{selectedIds.length} selected</Badge>
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    onClick={() => {
                                        const alreadySelectedIds = alreadySelectedEntities.map(e => e.Id);
                                        setSelectedIds(alreadySelectedIds);
                                        setSelectedEntities([...alreadySelectedEntities]);
                                    }}
                                    disabled={loading}
                                >
                                    Clear New Selection
                                </Button>
                            </>
                        ) : (
                            <Text size="xs" c="dimmed">
                                No items selected
                            </Text>
                        )}
                    </Group>
                </Box>

                <Group justify="center" align="center" gap="md" mb="md">
                    {totalPages > 1 && (
                        <Pagination
                            total={totalPages}
                            value={currentPage}
                            onChange={setCurrentPage}
                            disabled={loading}
                        />
                    )}
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={handleFullscreenToggle}
                        title={isTableFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                    >
                        {isTableFullscreen ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
                    </ActionIcon>
                </Group>

                <Box style={{
                    ...(isTableFullscreen && {
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        backgroundColor: 'var(--mantine-color-body)',
                        padding: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4
                    })
                }}>
                    <Flex direction="column" flex={1} mih={0}>
                        <EntityBrowserTable
                            entities={entities}
                            selectedIds={selectedIds}
                            loading={loading}
                            singleSelection={singleSelection}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            searchQuery={activeSearchQuery}
                            onEntitySelect={handleEntitySelect}
                            onSelectAll={handleSelectAll}
                            onSortChange={handleSortChange}
                            getDisplayName={getDisplayName}
                            getEntityType={getEntityType}
                            alreadySelectedEntities={alreadySelectedEntities}
                            newlySelectedEntities={selectedEntities.filter(e => !alreadySelectedEntities.some(ae => ae.Id === e.Id))}
                            isFullscreen={isTableFullscreen}
                        />
                    </Flex>

                    <Group justify="center" align="center" gap="md" mt={isTableFullscreen ? 'xs' : 'md'} mb={isTableFullscreen ? 0 : undefined}>
                        {totalPages > 1 && (
                            <Pagination
                                total={totalPages}
                                value={currentPage}
                                onChange={setCurrentPage}
                                disabled={loading}
                            />
                        )}
                        <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={handleFullscreenToggle}
                            title={isTableFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                        >
                            {isTableFullscreen ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
                        </ActionIcon>
                    </Group>
                </Box>

                {!isTableFullscreen && (
                    <Group justify="space-between" align="center" mt="md">
                        <Text size="sm" c="dimmed">
                            Showing {entities.length} of {totalResults} entities
                        </Text>
                        {activeSearchQuery && (
                            <Text size="sm" c="dimmed">
                                Filtered by: "{activeSearchQuery}"
                            </Text>
                        )}
                    </Group>
                )}
            </Stack>

            <Group
                justify="flex-end"
                gap="xs"
                pt="md"
                style={{ borderTop: '1px solid var(--mantine-color-default-border)', flexShrink: 0 }}
            >
                <Button variant="subtle" onClick={handleClose} disabled={loading}>
                    {viewOnly ? 'Close' : 'Cancel'}
                </Button>
                {!viewOnly && (
                    <Button
                        onClick={handleConfirm}
                        leftSection={<IconPlus size={16} />}
                        disabled={loading || selectedIds.length === 0 || !onConfirm}
                    >
                        {singleSelection
                            ? (selectedIds.length > 0 ? 'Add Selected' : 'Select Entity')
                            : `Add ${selectedIds.length} Selected`
                        }
                    </Button>
                )}
            </Group>
        </Modal>
    );
};

export default EntityBrowserDialog;
